<template>
  <!-- Add -->
  <div class="modal fade dq-cat-modal" id="add_asset_category" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md dq-cat-dialog">
      <div class="modal-content dq-modal-content" :key="addKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-indigo">
              <i class="ti ti-category"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">Add Category</h4>
              <div class="dq-subtitle">
                Create a clean bucket to organize assets (e.g., Laptops, Desks,
                Vehicles).
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitCreate">
          <div class="modal-body dq-body pb-0">
            <!-- Quick preview -->
            <div class="dq-preview mb-3">
              <div class="dq-preview-left">
                <div class="dq-preview-title">
                  <i class="ti ti-eye me-1"></i> Preview
                </div>
                <div class="dq-preview-sub">
                  <span class="dq-name">{{
                    form.name || "— Category name"
                  }}</span>
                  <span class="dq-dot">•</span>
                  <span class="dq-pill" :class="pillClass(form.status)">
                    <i class="ti ti-bolt"></i> {{ form.status || "Active" }}
                  </span>
                </div>
              </div>
              <div class="dq-preview-right">
                <span class="dq-mini">
                  <i class="ti ti-box"></i> Used by assets
                </span>
              </div>
            </div>

            <div class="dq-card">
              <div class="dq-card-head">
                <span class="dq-badge dq-badge-indigo">
                  <i class="ti ti-edit-circle"></i>
                </span>
                <div>
                  <div class="dq-h6">Category Details</div>
                  <div class="dq-help">
                    Keep names short and consistent for easier filtering.
                  </div>
                </div>
              </div>

              <div class="dq-card-body">
                <div class="mb-3">
                  <label class="form-label dq-label">
                    Category Name <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.name"
                    class="form-control dq-input"
                    placeholder="e.g. Laptops"
                  />
                  <div class="dq-hint">
                    Tip: Use plural nouns (Laptops, Printers).
                  </div>
                </div>

                <div class="mb-2">
                  <label class="form-label dq-label">Status</label>
                  <div class="dq-status-row">
                    <select v-model="form.status" class="form-select dq-input">
                      <option>Active</option>
                      <option>Inactive</option>
                    </select>

                    <div class="dq-status-chip" :class="pillClass(form.status)">
                      <i class="ti ti-bolt"></i>
                      <span>{{ form.status }}</span>
                    </div>
                  </div>

                  <div class="dq-hint">
                    Inactive categories remain in history but won’t be used for
                    new assets.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-footer">
            <button
              type="button"
              class="btn btn-white border dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button type="submit" class="btn btn-primary dq-btn dq-btn-primary">
              <span v-if="busy" class="spinner-border spinner-border-sm me-2" />
              Add Category
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Edit -->
  <div class="modal fade dq-cat-modal" id="edit_asset_category" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md dq-cat-dialog">
      <div class="modal-content dq-modal-content" :key="editKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-blue">
              <i class="ti ti-edit"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">Edit Category</h4>
              <div class="dq-subtitle">
                Update label and status. Changes reflect across assets using
                this category.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitEdit">
          <div class="modal-body dq-body pb-0">
            <!-- Quick preview -->
            <div class="dq-preview mb-3">
              <div class="dq-preview-left">
                <div class="dq-preview-title">
                  <i class="ti ti-eye me-1"></i> Preview
                </div>
                <div class="dq-preview-sub">
                  <span class="dq-name">{{
                    form.name || "— Category name"
                  }}</span>
                  <span class="dq-dot">•</span>
                  <span class="dq-pill" :class="pillClass(form.status)">
                    <i class="ti ti-bolt"></i> {{ form.status || "Active" }}
                  </span>
                </div>
              </div>
              <div class="dq-preview-right">
                <span class="dq-mini">
                  <i class="ti ti-refresh"></i> Updates everywhere
                </span>
              </div>
            </div>

            <div class="dq-card">
              <div class="dq-card-head">
                <span class="dq-badge dq-badge-blue">
                  <i class="ti ti-settings"></i>
                </span>
                <div>
                  <div class="dq-h6">Category Details</div>
                  <div class="dq-help">
                    Edit carefully to avoid confusion in reports.
                  </div>
                </div>
              </div>

              <div class="dq-card-body">
                <div class="mb-3">
                  <label class="form-label dq-label">
                    Category Name <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.name"
                    class="form-control dq-input"
                    placeholder="e.g. Office Chairs"
                  />
                </div>

                <div class="mb-2">
                  <label class="form-label dq-label">Status</label>
                  <div class="dq-status-row">
                    <select v-model="form.status" class="form-select dq-input">
                      <option>Active</option>
                      <option>Inactive</option>
                    </select>

                    <div class="dq-status-chip" :class="pillClass(form.status)">
                      <i class="ti ti-bolt"></i>
                      <span>{{ form.status }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-footer">
            <button
              type="button"
              class="btn btn-white border dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="!isEdit"
            >
              <span v-if="busy" class="spinner-border spinner-border-sm me-2" />
              Save Changes
              <i class="ti ti-check ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete (unchanged) -->
  <div class="modal fade" id="delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">
            Are you sure you want to delete this asset category?
          </p>
          <div class="d-flex justify-content-center">
            <a
              href="javascript:void(0);"
              class="btn btn-light me-3"
              data-bs-dismiss="modal"
              >Cancel</a
            >
            <button
              class="btn btn-danger"
              :disabled="deleting"
              @click="confirmDelete"
            >
              <span
                v-if="deleting"
                class="spinner-border spinner-border-sm me-1"
              />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useAssetCategoryStore } from "@/stores/assetCategoryStore";
import { toast } from "@/utils/toast";

const props = defineProps({ editItem: { type: Object, default: null } });
const emit = defineEmits(["saved"]);

const store = useAssetCategoryStore();
const deleting = ref(false);
const busy = computed(() => store.loading);

// keys to force-refresh form internals
const addKey = ref(0);
const editKey = ref(0);

const form = ref(blank());

function blank() {
  return { name: "", status: "Active" };
}
function resetForm() {
  const b = blank();
  for (const k in b) form.value[k] = b[k];
}

watch(
  () => props.editItem,
  (val) => {
    if (val) {
      form.value.name = val.name ?? "";
      form.value.status = val.status ?? "Active";
      editKey.value++;
    } else {
      resetForm();
    }
  },
  { immediate: true }
);

const isEdit = computed(() => !!props.editItem?.id);

async function submitCreate() {
  if (!form.value.name) {
    toast("Category name is required", "error");
    return;
  }
  await store.create({ ...form.value });
  closeNew();
  resetForm();
  addKey.value++;
  emit("saved");
}

async function submitEdit() {
  if (!props.editItem?.id) return;
  await store.update(props.editItem.id, { ...form.value });
  closeEdit();
  resetForm();
  editKey.value++;
  emit("saved");
}

function closeNew() {
  document
    .querySelector('#add_asset_category [data-bs-dismiss="modal"]')
    ?.click();
}
function closeEdit() {
  document
    .querySelector('#edit_asset_category [data-bs-dismiss="modal"]')
    ?.click();
}

async function confirmDelete() {
  deleting.value = true;
  try {
    if (!props.editItem?.id) return;
    await store.remove(props.editItem.id);
    emit("saved");
  } catch (e) {
    console.error(e);
  } finally {
    deleting.value = false;
  }
}

/* UI-only helpers */
function pillClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "dq-pill-live";
  return "dq-pill-draft";
}

let cleanup = null;
onMounted(() => {
  const addEl = document.getElementById("add_asset_category");
  const editEl = document.getElementById("edit_asset_category");

  const handleAddShow = () => {
    resetForm();
    addKey.value++;
  };
  const handleAddHidden = () => resetForm();
  const handleEditHidden = () => {
    resetForm();
    editKey.value++;
  };

  addEl?.addEventListener("show.bs.modal", handleAddShow);
  addEl?.addEventListener("hidden.bs.modal", handleAddHidden);
  editEl?.addEventListener("hidden.bs.modal", handleEditHidden);

  cleanup = () => {
    addEl?.removeEventListener("show.bs.modal", handleAddShow);
    addEl?.removeEventListener("hidden.bs.modal", handleAddHidden);
    editEl?.removeEventListener("hidden.bs.modal", handleEditHidden);
  };
});
onBeforeUnmount(() => cleanup && cleanup());
</script>

<style scoped>
/* Slightly wider and premium */
.dq-cat-dialog {
  max-width: 640px;
}
@media (max-width: 768px) {
  .dq-cat-dialog {
    max-width: 96%;
  }
}

.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}

/* Header */
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.14), #fff);
}
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.16);
  color: rgb(79, 70, 229);
}
.dq-hero-blue {
  background: rgba(59, 130, 246, 0.14);
  color: rgb(37, 99, 235);
}

/* Body */
.dq-body {
  padding: 18px 22px 10px;
}

/* Preview */
.dq-preview {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
}
.dq-preview-title {
  font-weight: 950;
}
.dq-preview-sub {
  margin-top: 2px;
  color: rgba(15, 23, 42, 0.68);
  font-size: 0.86rem;
}
.dq-name {
  font-weight: 950;
}
.dq-dot {
  margin: 0 8px;
  opacity: 0.5;
}
.dq-mini {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  font-weight: 800;
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.78);
}

/* Card */
.dq-card {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  background: #fff;
  overflow: hidden;
}
.dq-card-head {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 14px 14px 0 14px;
}
.dq-card-body {
  padding: 14px;
}
.dq-h6 {
  font-weight: 950;
  letter-spacing: -0.2px;
}
.dq-help {
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
  line-height: 1.35;
}
.dq-hint {
  margin-top: 6px;
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Badge */
.dq-badge {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.dq-badge-indigo {
  background: rgba(99, 102, 241, 0.12);
  color: rgb(79, 70, 229);
}
.dq-badge-blue {
  background: rgba(59, 130, 246, 0.12);
  color: rgb(37, 99, 235);
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.55);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14);
}

/* Status row */
.dq-status-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.dq-status-chip {
  min-width: 120px;
  justify-content: center;
}

/* Pills */
.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border-radius: 999px;
  font-weight: 950;
  font-size: 0.78rem;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-pill-live {
  background: rgba(34, 197, 94, 0.1);
  color: rgb(22, 163, 74);
  border-color: rgba(34, 197, 94, 0.18);
}
.dq-pill-draft {
  background: rgba(15, 23, 42, 0.05);
  color: rgba(15, 23, 42, 0.75);
}

/* Footer */
.dq-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}
</style>
